import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { Alert, Box, Button, Chip, Paper, Skeleton, Typography } from "@mui/material";
import { getMessageSettings } from "../api/actions";
import { canManagePeople } from "../accessRoles";
import MessageEditor, { MESSAGE_TYPES, isShowing, showsOn } from "./MessageEditor";

// Messages shown on the customer portal sign-in page and the public website.
function CustomerSite(props) {
  const messages = props.setting.items || [];
  const canEdit = canManagePeople(props.user.details);
  const [loading, setLoading] = useState(true);
  const [editingUuid, setEditingUuid] = useState(null); // a message uuid, "new", or null

  const load = () => {
    setLoading(true);
    Promise.resolve(props.getMessageSettings(false)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const sorted = [...messages].sort((a, b) => Number(isShowing(b)) - Number(isShowing(a)) || String(a.name).localeCompare(String(b.name)));
  const showingCount = messages.filter(isShowing).length;
  const editing = messages.find((m) => m.uuid === editingUuid) || null;
  const mainMessage = messages.find((m) => m.name === "main") || null;

  if (loading && messages.length === 0) {
    return (
      <Box sx={{ display: "grid", gap: 2 }}>
        {[0, 1].map((i) => <Skeleton key={i} variant="rounded" height={110} />)}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "75ch" }}>
          Messages that are on appear on the customer portal sign-in page and on the public website.
          {messages.length > 0 && ` ${showingCount} of ${messages.length} showing now.`}
        </Typography>
        {canEdit && (
          <Button variant="contained" color="secondary" size="small" onClick={() => setEditingUuid("new")} sx={{ textTransform: "none" }}>
            New message
          </Button>
        )}
      </Box>

      {messages.length === 0 ? (
        <Alert severity="info" action={<Button color="inherit" size="small" onClick={load} sx={{ textTransform: "none" }}>Try again</Button>}>
          There are no customer site messages yet, or they didn't load.
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          {sorted.map((m, i) => {
            const on = isShowing(m);
            const type = MESSAGE_TYPES.find((t) => t.value === m.type) || MESSAGE_TYPES[0];
            return (
              <Box
                key={m.uuid}
                role="button"
                tabIndex={0}
                onClick={() => setEditingUuid(m.uuid)}
                onKeyDown={(e) => { if (e.key === "Enter") setEditingUuid(m.uuid); }}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "6px minmax(0,1fr)", md: "6px minmax(0,1fr) auto" },
                  gap: 2,
                  alignItems: "start",
                  px: 2.5,
                  py: 2,
                  borderTop: i === 0 ? 0 : 1,
                  borderColor: "divider",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box sx={{ alignSelf: "stretch", borderRadius: 1, bgcolor: type.bg, opacity: on ? 1 : 0.35 }} aria-hidden="true" />
                <Box sx={{ minWidth: 0, display: "grid", gap: 0.5 }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{m.title || "Untitled"}</Typography>
                    <Chip
                      label={on ? "Showing" : "Hidden"}
                      size="small"
                      color={on ? "success" : "default"}
                      variant={on ? "filled" : "outlined"}
                      sx={{ height: 22 }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere" }}
                  >
                    {m.message}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">{m.name} · shows on</Typography>
                    {showsOn(m.name).map((place) => (
                      <Chip key={place} label={place} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
                    ))}
                  </Box>
                </Box>
                <Box sx={{ gridColumn: { xs: "2", md: "auto" }, display: "grid", justifyItems: { xs: "start", md: "end" }, gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Updated {moment(m.updated).format("MMM D, YYYY")}</Typography>
                  <Button size="small" variant="outlined" sx={{ textTransform: "none" }} onClick={(e) => { e.stopPropagation(); setEditingUuid(m.uuid); }}>
                    {canEdit ? "Edit" : "View"}
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}

      <MessageEditor
        open={editingUuid !== null}
        message={editing}
        mainMessage={mainMessage}
        canEdit={canEdit}
        onClose={() => setEditingUuid(null)}
      />
    </Box>
  );
}

function mapStateToProps(state) {
  return {
    setting: state.setting,
    user: state.user,
  };
}

export default connect(mapStateToProps, { getMessageSettings })(CustomerSite);
